import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Box,
  Typography,
  Alert,
  Chip
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { enUS, enAU } from 'date-fns/locale';
import openScheduleService, { type OpenSession } from '../services/openScheduleService';

interface AdhocSessionDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (session: OpenSession) => void;
  scheduleId: string;
  agencySlug: string;
  propertyId?: string;
  propertyAddress?: string;
  existingSessions?: OpenSession[];
}

interface AdhocSessionFormData {
  date: Date | null;
  startTime: string;
  endTime: string;
  notes: string;
  maxAttendees: number;
}

const normalizeDate = (date: Date | null) => {
  if (!date) return null;
  const normalized = new Date(date);
  normalized.setHours(0, 0, 0, 0);
  return normalized;
};

const getAdapterLocale = () => {
  const navLang = typeof navigator !== 'undefined' ? navigator.language : 'en-US';
  const lower = navLang.toLowerCase();
  if (lower.startsWith('en-au')) return enAU;
  return enUS;
};

const formatLocalDate = (date: Date) => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const addMinutesToTime = (time: string, minutes: number) => {
  if (!time) return time;

  const [hours, mins] = time.split(':').map(Number);
  const date = new Date();
  date.setHours(Number.isNaN(hours) ? 0 : hours, Number.isNaN(mins) ? 0 : mins, 0, 0);
  date.setMinutes(date.getMinutes() + minutes);

  const hh = `${date.getHours()}`.padStart(2, '0');
  const mm = `${date.getMinutes()}`.padStart(2, '0');

  return `${hh}:${mm}`;
};

const AdhocSessionDialog: React.FC<AdhocSessionDialogProps> = ({
  open,
  onClose,
  onSuccess,
  scheduleId,
  agencySlug,
  propertyId: _propertyId,
  propertyAddress,
  existingSessions = []
}) => {
  const adapterLocale = getAdapterLocale();
  const [formData, setFormData] = useState<AdhocSessionFormData>({
    date: normalizeDate(new Date()),
    startTime: '10:00',
    endTime: '11:00',
    notes: '',
    maxAttendees: 20
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!formData.date) {
      setError('Please select a date');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const sessionData = {
        date: formatLocalDate(formData.date), // Local YYYY-MM-DD to avoid TZ shift
        startTime: formData.startTime,
        endTime: formData.endTime,
        notes: formData.notes || undefined,
        maxAttendees: formData.maxAttendees
      };

      const newSession = await openScheduleService.addAdhocSession(
        scheduleId,
        sessionData,
        agencySlug
      );

      onSuccess(newSession);
      handleClose();
    } catch (err: any) {
      let errorMessage = err.message || 'Failed to create ad-hoc session';
      
      // Provide helpful suggestions for common errors
      if (errorMessage.includes('already exists')) {
        errorMessage = `${errorMessage}\n\nTry:\n• Choosing a different date\n• Selecting a different time slot\n• Checking existing sessions in the table`;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      date: normalizeDate(new Date()),
      startTime: '10:00',
      endTime: '11:00',
      notes: '',
      maxAttendees: 20
    });
    setError(null);
    onClose();
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={adapterLocale}>
      <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
        <DialogTitle>
          Add Ad-Hoc Open House Session
          {propertyAddress && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {propertyAddress}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              <Box sx={{ whiteSpace: 'pre-line' }}>
                {error}
              </Box>
            </Alert>
          )}

          <Box sx={{ pt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <DatePicker
                  label="Session Date"
                  value={formData.date}
                  onChange={(date) => setFormData({ ...formData, date: normalizeDate(date) })}
                  minDate={new Date()} // Cannot select past dates
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true
                    }
                  }}
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  label="Start Time"
                  type="time"
                  value={formData.startTime}
                  onChange={(e) => {
                    const newStart = e.target.value;
                    setFormData({
                      ...formData,
                      startTime: newStart,
                      endTime: addMinutesToTime(newStart, 30)
                    });
                  }}
                  fullWidth
                  required
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  label="End Time"
                  type="time"
                  value={formData.endTime}
                  onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
                  fullWidth
                  required
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  label="Max Attendees"
                  type="number"
                  value={formData.maxAttendees}
                  onChange={(e) => setFormData({ ...formData, maxAttendees: parseInt(e.target.value) || 20 })}
                  fullWidth
                  inputProps={{ min: 1, max: 100 }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Notes (Optional)"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="Special instructions, requirements, or notes for this session..."
                />
              </Grid>

              {/* Show existing sessions for selected date */}
              {formData.date && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                    Existing sessions on {formData.date.toLocaleDateString((typeof navigator !== 'undefined' && navigator.language) ? navigator.language : undefined)}:
                  </Typography>
                  {existingSessions
                    .filter(session => {
                      const sessionDate = new Date(session.date);
                      return sessionDate.toDateString() === formData.date?.toDateString();
                    })
                    .map((session, index) => (
                      <Chip
                        key={index}
                        label={`${session.startTime} - ${session.endTime}${session.isAdhoc ? ' (Ad-hoc)' : ''}`}
                        size="small"
                        sx={{ mr: 1, mb: 1 }}
                        color={session.startTime === formData.startTime ? 'error' : 'default'}
                      />
                    ))}
                  {existingSessions.filter(session => {
                    const sessionDate = new Date(session.date);
                    return sessionDate.toDateString() === formData.date?.toDateString();
                  }).length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                      No existing sessions on this date
                    </Typography>
                  )}
                </Grid>
              )}

            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            disabled={loading || !formData.date}
          >
            {loading ? 'Creating...' : 'Add Session'}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default AdhocSessionDialog;
